import { MaintenanceBreakService } from "@app/shared/settings/system-settings/services/maintenance-break.service";
import { AuthService } from "../services/auth.service";
import { catchError, map, Observable, of, switchMap } from "rxjs";
import { IInitialSessionDetails, ISession } from "@app/shared/general/sessions/interface/session.interface";

/**
 * Application initializer for authentication and maintenance break handling.
 *
 * This function is intended to be used in Angular's `APP_INITIALIZER` to
 * ensure that user authentication state is loaded before the application starts.
 * It also checks for an active maintenance break and optionally refreshes its state.
 *
 * Behavior:
 * 1. Calls `authService.refreshAuthData(true, false)` to load or refresh the current session.
 * 2. If the session has `skipMaintenanceBreak` set to true, it calls
 *    `maintenanceBreakService.refreshMaintenanceBreak()` before proceeding.
 * 3. In case of errors:
 *    - If a maintenance break is active, returns `null` (blocking normal session).
 *    - Otherwise, logs out the user and returns `null`.
 *
 * @param authService - The authentication service used to refresh session data.
 * @param maintenanceBreakService - The service responsible for managing maintenance breaks.
 * @returns An `Observable` that emits the current session (`ISession` or `IInitialSessionDetails`) or `null`.
 *
 * @example
 * ```ts
 * providers: [
 *   {
 *     provide: APP_INITIALIZER,
 *     useFactory: authInitializer,
 *     deps: [AuthService, MaintenanceBreakService],
 *     multi: true
 *   }
 * ]
 * ```
 */
export function authInitializer(
  authService: AuthService,
  maintenanceBreakService: MaintenanceBreakService
): Observable<ISession | IInitialSessionDetails | null> {
  return authService.refreshAuthData(true, false).pipe(
    switchMap(session => {
      if (session && "skipMaintenanceBreak" in session && session.skipMaintenanceBreak) {
        // Refresh maintenance break state if the user session allows skipping it
        return maintenanceBreakService.refreshMaintenanceBreak().pipe(map(() => session));
      }
      return of(session);
    }),
    catchError(() =>
      // If a maintenance break is active, block login by returning null.
      // Otherwise, logout the user and return null.
      maintenanceBreakService.maintenanceBreakIsActiveSnapshot ? of(null) : authService.logout().pipe(map(() => null))
    )
  );
}
